import { Component, EventEmitter, Input, Output, computed, signal } from '@angular/core';
import { AQASearchFacets, AQASearchRequest } from '../../models';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-aqa-filters',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './aqa-filters.component.html',
  styleUrl: './aqa-filters.component.scss'
})
export class AqaFiltersComponent {
  @Input() facets: AQASearchFacets | null = null;
  @Input() activeFilters: Partial<AQASearchRequest> = {};
  @Output() filtersChange = new EventEmitter<Partial<AQASearchRequest>>();
  @Output() clearFilters = new EventEmitter<void>();

  // Local state
  showAllSenders = signal(false);

  // Computed from activeFilters
  selectedSender = computed(() => this.activeFilters.fromFilter ?? null);
  hasAttachments = computed(() => this.activeFilters.hasAttachments ?? false);
  selectedAttachmentTypes = computed(() => this.activeFilters.attachmentTypes ?? []);
  dateFrom = computed(() => this.activeFilters.dateFrom?.split('T')[0] ?? '');
  dateTo = computed(() => this.activeFilters.dateTo?.split('T')[0] ?? '');

  private activeDateRange: number | null = null;

  // =========================================================================
  // Date Range
  // =========================================================================

  setDateRange(days: number): void {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);

    this.activeDateRange = days;
    this.emitFilters({
      dateFrom: from.toISOString(),
      dateTo: to.toISOString()
    });
  }

  isDateRangeActive(days: number): boolean {
    return this.activeDateRange === days;
  }

  onDateFromChange(value: string): void {
    this.activeDateRange = null;
    this.emitFilters({ dateFrom: value ? new Date(value).toISOString() : undefined });
  }

  onDateToChange(value: string): void {
    this.activeDateRange = null;
    this.emitFilters({ dateTo: value ? new Date(value).toISOString() : undefined });
  }

  // =========================================================================
  // Sender
  // =========================================================================

  onSenderClick(sender: string): void {
    const current = this.selectedSender();
    this.emitFilters({ fromFilter: current === sender ? undefined : sender });
  }

  // =========================================================================
  // Attachments
  // =========================================================================

  onHasAttachmentsChange(): void {
    this.emitFilters({ hasAttachments: !this.hasAttachments() || undefined });
  }

  onAttachmentTypeClick(type: string): void {
    const current = this.selectedAttachmentTypes();
    const updated = current.includes(type)
      ? current.filter((t: any) => t !== type)
      : [...current, type];

    this.emitFilters({ attachmentTypes: updated.length > 0 ? updated : undefined });
  }

  // =========================================================================
  // Clear
  // =========================================================================

  onClearAll(): void {
    this.activeDateRange = null;
    this.activeFilters = {
      attachmentTypes: undefined
      , dateFrom: undefined
      , dateTo: undefined
      , fromFilter: undefined
      , hasAttachments: undefined
      , toFilter: undefined
    };
    this.clearFilters.emit();
  }

  // =========================================================================
  // Emit
  // =========================================================================

  private emitFilters(changes: Partial<AQASearchRequest>): void {
    const updated = { ...this.activeFilters, ...changes };

    // Clean up undefined values
    Object.keys(updated).forEach(key => {
      if (updated[key as keyof AQASearchRequest] === undefined) {
        delete updated[key as keyof AQASearchRequest];
      }
    });

    this.filtersChange.emit(updated);
  }
}
